import React from 'react'
import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import Button from 'components/Button'
import { ToggleGroup, ToggleItem } from 'components/ToggleGroup'
import { Form, FormRow, SelectContainer } from 'components/inputs'
import FormGroup from 'components/inputs/FormGroup'
import TextInput from 'components/inputs/TextInput'
import {
  NotificationBar,
  NotificationBarSubtitle,
  NotificationBarTitle,
} from 'components/NotificationBar'
import {
  allBookkeepingPlatforms,
  availableBookkeepingSoftwareTypes,
  BookkeepingPlatform,
  formatAvailableBookkeepingSoftware,
  formatBookkeepingPlatform,
  formatBookkeepingPlatformLoginName,
  getBookkeepingPlatformLoginUrl,
} from 'jobs/bookkeeping/BookkeepingSoftware'
import BookkeepingGrantAccessDialogViewModel from './BookkeepingGrantAccessDialogViewModel'

const testId = 'bookkeeping-grant-access-dialog'

interface BookkeepingGrantAccessDialogProps {
  vm: BookkeepingGrantAccessDialogViewModel
}

export default observer(function BookkeepingGrantAccessDialog({
  vm,
}: BookkeepingGrantAccessDialogProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton />
      {vm.page.type === 'SelectSoftware' ? (
        <SelectSoftwarePage vm={vm} />
      ) : (
        <InstructionsPage vm={vm} platform={vm.page.platform} />
      )}
    </ModalSheet>
  )
})

/**
 * Lets the user pick the platform the client keeps their books in.
 */
const SelectSoftwarePage = observer(function SelectSoftwarePage({
  vm,
}: BookkeepingGrantAccessDialogProps) {
  return (
    <>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            {vm.isClientPerspective
              ? 'Share access to your books'
              : `Share access to the client's books`}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            {vm.isClientPerspective
              ? 'Select your bookkeeping account to get started.'
              : `Select the client's bookkeeping account to get started.`}
          </ModalSheetContentDescription>
          {/*
           * Nothing is ever left `toggled` here - picking a platform navigates
           * straight to its instructions. ToggleItem is used for the click,
           * keyboard and focus handling it brings with it.
           */}
          <ToggleGroup stretch>
            {allBookkeepingPlatforms.map((platform) => (
              <ToggleItem
                key={platform}
                onChange={() => vm.selectPlatform(platform)}
                dataTest={`${testId}-software-${platform}`}
              >
                {formatBookkeepingPlatform(platform)}
              </ToggleItem>
            ))}
          </ToggleGroup>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            dataTest={`${testId}-close-button`}
          >
            Close
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
})

interface InstructionsPageProps extends BookkeepingGrantAccessDialogProps {
  platform: BookkeepingPlatform
}

/**
 * Walks the user through granting the Pro access, and collects the
 * confirmation that they did.
 */
const InstructionsPage = observer(function InstructionsPage({
  vm,
  platform,
}: InstructionsPageProps) {
  const isClient = vm.isClientPerspective
  const loginUrl = getBookkeepingPlatformLoginUrl(platform)
  const pro = vm.pro

  return (
    <>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            {platform === 'Other'
              ? isClient
                ? 'Select your bookkeeping software'
                : `Select the client's bookkeeping software`
              : isClient
                ? 'Add your Pro to your books'
                : `Add the Pro to the client's books`}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            <PlatformInstructions platform={platform} isClient={isClient} />
          </ModalSheetContentDescription>

          <Form>
            {platform === 'Other' && (
              <>
                <FormRow>
                  <SelectContainer
                    label="Bookkeeping software"
                    model={vm}
                    field="otherSoftware"
                    items={availableBookkeepingSoftwareTypes.map(
                      (type) =>
                        [type, formatAvailableBookkeepingSoftware(type)] as [
                          string,
                          string,
                        ],
                    )}
                    data-test={`${testId}-other-software-select`}
                  />
                </FormRow>
                <FormRow>
                  <OtherSoftwareInstructions
                    softwareName={formatAvailableBookkeepingSoftware(
                      vm.otherSoftware,
                    )}
                    isClient={isClient}
                  />
                </FormRow>
              </>
            )}

            {vm.hasProDetails && pro ? (
              <>
                <FormRow>
                  <CopyTextField
                    label="First name"
                    value={pro.givenName}
                    onCopy={() => vm.copyToClipboard(pro.givenName)}
                    dataTest={`${testId}-copy-given-name-button`}
                  />
                </FormRow>
                <FormRow>
                  <CopyTextField
                    label="Last name"
                    value={pro.familyName}
                    onCopy={() => vm.copyToClipboard(pro.familyName)}
                    dataTest={`${testId}-copy-family-name-button`}
                  />
                </FormRow>
                <FormRow>
                  <CopyTextField
                    label="Email address"
                    value={pro.email}
                    onCopy={() => vm.copyToClipboard(pro.email)}
                    dataTest={`${testId}-copy-email-button`}
                  />
                </FormRow>
              </>
            ) : (
              <FormRow>
                <MissingProDetails isClient={isClient} />
              </FormRow>
            )}

            {!isClient && (
              <FormRow>
                <NotificationBar dataTest={`${testId}-admin-note`}>
                  <NotificationBarSubtitle>
                    Only confirm once the client has actually shared access. The
                    client will not be notified that you did this on their
                    behalf.
                  </NotificationBarSubtitle>
                </NotificationBar>
              </FormRow>
            )}
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.goBack}
            disabled={vm.confirmAccessProvided.pending}
            dataTest={`${testId}-back-button`}
          >
            Back
          </Button>
          {loginUrl && (
            <Button
              type="default"
              href={loginUrl}
              target="_blank"
              dataTest={`${testId}-open-platform-button`}
            >
              Open {formatBookkeepingPlatformLoginName(platform)}
            </Button>
          )}
          <Button
            type="primary"
            onClick={vm.confirmAccessProvided}
            loading={vm.confirmAccessProvided.pending}
            disabled={vm.confirmAccessProvided.pending || !vm.hasProDetails}
            dataTest={`${testId}-access-provided-button`}
          >
            Access provided
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
})

interface PlatformInstructionsProps {
  platform: BookkeepingPlatform
  isClient: boolean
}

/**
 * The steps for granting access in the selected platform.
 */
function PlatformInstructions({
  platform,
  isClient,
}: PlatformInstructionsProps) {
  switch (platform) {
    case 'QuickBooks':
      return (
        <>
          {isClient ? (
            <QuickBooksClientInstructions />
          ) : (
            <QuickBooksAdminInstructions />
          )}
          <br />
          <ConfirmationHint isClient={isClient} />
        </>
      )
    case 'Xero':
      return (
        <>
          {isClient ? <XeroClientInstructions /> : <XeroAdminInstructions />}
          <br />
          <ConfirmationHint isClient={isClient} />
        </>
      )
    case 'Other':
      return (
        <>
          {isClient ? (
            <OtherSoftwareClientInstructions />
          ) : (
            <OtherSoftwareAdminInstructions />
          )}
        </>
      )
  }
}

interface ConfirmationHintProps {
  isClient: boolean
}

/**
 * Points the user at the button that completes the step.
 */
function ConfirmationHint({ isClient }: ConfirmationHintProps) {
  return (
    <p>
      Once granted, click <b>Access provided</b> so{' '}
      {isClient ? 'your Pro' : 'the Pro'} can get started.
    </p>
  )
}

interface OtherSoftwareInstructionsProps {
  softwareName: string
  isClient: boolean
}

/**
 * The steps for granting access in a software we have no walkthrough for.
 */
function OtherSoftwareInstructions({
  softwareName,
  isClient,
}: OtherSoftwareInstructionsProps) {
  return (
    <p>
      In <b>{softwareName}</b>,{' '}
      {isClient
        ? 'invite your Pro as a user with accounting access'
        : 'the client should invite the Pro as a user with accounting access'}{' '}
      using the details below. Then click <b>Access provided</b> so{' '}
      {isClient ? 'your Pro' : 'the Pro'} can get started.
    </p>
  )
}

interface CopyTextFieldProps {
  label: string
  value: string
  onCopy: () => unknown
  dataTest?: string
}

/**
 * A read-only field with a button that copies its value.
 */
function CopyTextField({ label, value, onCopy, dataTest }: CopyTextFieldProps) {
  return (
    <FormGroup label={label}>
      <TextInput
        value={value}
        readOnly
        button={
          <Button type="text" onClick={onCopy} dataTest={dataTest}>
            Copy
          </Button>
        }
      />
    </FormGroup>
  )
}

interface MissingProDetailsProps {
  isClient: boolean
}

/**
 * Shown when we could not resolve the Pro's details.
 */
function MissingProDetails({ isClient }: MissingProDetailsProps) {
  return (
    <NotificationBar type="error" dataTest={`${testId}-missing-pro-details`}>
      <NotificationBarTitle>
        We couldn't load {isClient ? 'your' : 'the'} Pro's details
      </NotificationBarTitle>
      <NotificationBarSubtitle>
        Please refresh the page and try again. If the problem persists, let us
        know in the job conversation.
      </NotificationBarSubtitle>
    </NotificationBar>
  )
}

/**
 * QuickBooks instructions for clients.
 */
function QuickBooksClientInstructions() {
  return (
    <p>
      Go to <b>Manage users</b> in your QuickBooks settings. Under the{' '}
      <b>Accounting firms</b> tab, click <b>Invite</b>. Use the information
      below to complete the necessary fields to add us to your books.
    </p>
  )
}

/**
 * QuickBooks instructions for admins.
 */
function QuickBooksAdminInstructions() {
  return (
    <p>
      The client should go to <b>Manage users</b> in their QuickBooks settings.
      Under the <b>Accounting firms</b> tab, click <b>Invite</b>. Use the
      information below to complete the necessary fields to add us to their
      books.
    </p>
  )
}

/**
 * Xero instructions for clients.
 */
function XeroClientInstructions() {
  return (
    <p>
      Click on your organization name in Xero and select <b>Settings</b>, then
      click <b>Users</b>. Click <b>Invite a user</b> and enter your Pro's
      details below. Under Permissions, check <b>Business and accounting</b>,
      choose the <b>Advisor</b> role, then click <b>Send invite</b>.
    </p>
  )
}

/**
 * Xero instructions for admins.
 */
function XeroAdminInstructions() {
  return (
    <p>
      The client should click their organization name in Xero and select{' '}
      <b>Settings</b>, then click <b>Users</b>. Click <b>Invite a user</b> and
      enter the Pro's details below. Under Permissions, check{' '}
      <b>Business and accounting</b>, choose the <b>Advisor</b> role, then click{' '}
      <b>Send invite</b>.
    </p>
  )
}

/**
 * Other software instructions for clients.
 */
function OtherSoftwareClientInstructions() {
  return (
    <p>
      Choose the software you use, then add your Pro with the details below so
      they can get started right away.
    </p>
  )
}

/**
 * Other software instructions for admins.
 */
function OtherSoftwareAdminInstructions() {
  return (
    <p>
      Choose the software the client uses, then add the Pro with the details
      below so they can get started right away.
    </p>
  )
}
